import { once } from 'ramda';
import { useEffect, useRef, useState } from 'react';
import { validateGeolocation } from 'js/hooks/auth/pas';
import { htcmdService } from 'js/services/htcmd.service';

import * as constants from '../constants';

export const useScheduler = ({
    sdk,
    nextTime,
    license,
    transactionID,
    userId,
    identifierType,
    setTransactionID,
    triggerTimestampRef,
    setLicense,
    setNextTime,
    triggerGeolocation,
    requestPacket
}) => {
    const [retry, setRetry] = useState(0);
    const isVisibleAndFocused = useRef(!document.hidden && document.hasFocus());

    useEffect(() => {
        if (!sdk || !userId) {
            return;
        }

        const handler = () => isVisibleAndFocused.current = !document.hidden && document.hasFocus();

        document.addEventListener('visibilitychange', handler);
        window.addEventListener('focus', handler);

        return () => {
            document.removeEventListener('visibilitychange', handler);
            window.removeEventListener('focus', handler);
        };
    }, [sdk, userId, isVisibleAndFocused]);

    useEffect(() => {
        if (!sdk || !userId) {
            return;
        }

        if (!license || !transactionID) {
            return;
        }

        const timeout = setTimeout(() => {
            if (!isVisibleAndFocused.current && !htcmdService.isWrapperEnabled) {
                const handler = once(() => triggerGeolocation({ reason: constants.GEOLOCATION_REASON.FOREGROUND_CHECK }));

                document.addEventListener('visibilitychange', handler, { once: true });
                window.addEventListener('focus', handler, { once: true });
                return;
            }

            requestPacket();
        }, nextTime);

        return () => {
            // unset current timeout
            triggerTimestampRef.current = null;
            setTransactionID(null);
            setLicense(null);
            setNextTime(0);
            setRetry(0);
            clearTimeout(timeout);
        };
    }, [
        sdk,
        retry,
        nextTime,
        license,
        transactionID,
        userId,
        identifierType,
        triggerGeolocation,
        triggerTimestampRef,
        setTransactionID,
        setLicense,
        setNextTime,
        requestPacket
    ]);

    /**
     * Each 20 seconds make a request to IMS in order to trigger IP change, if IP of the player has changed.
     */
    useEffect(() => {
        if (!sdk || !userId || !license || !transactionID || !nextTime) {
            return;
        }

        const interval = setInterval(() => {
            validateGeolocation(null, null, null, null, null, null);
        }, 20000);

        return () => {
            clearInterval(interval);
        };
    }, [
        sdk,
        userId,
        license,
        transactionID,
        nextTime
    ]);
};
